import React, { useCallback, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import useUiuxStore from 'src/store/useUiuxStore';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { base64ToBlob, downloadExcel, downloadSettingExcel } from 'src/utils/blob/blobUtil';
import { ShowSuccessFetch, showSuccess, showWarning } from 'src/utils/toast/toastUtil';
import {
    downloadExcel_HRM1030_R01, downloadExcel_HRM1030_R01Training, downloadHRM1030_R01File,
    getHRM1030_R01AllCount, getHRM1030_R01FileList, getHRM1030_R01List, getHRM1030_R01TrainingList,
    HRM1030_R01Params, patchHRM1030_R01,
} from './Api_HRM1030_R01';
import {
    defaultHRM1030_R01FileDto, HRM1030_R01FileDto, HRM1030_R01HeaderDto, HRM1030_R01HeaderGridRow,
    HRM1030_R01TrainingDto,
} from './Dto_HRM1030_R01';
import { HRM1030_R01Filter, useHRM1030_R01Store } from './Store_HRM1030_R01';
import {
    getHRM1030_R01MimeType, isHRM1030_R01Pdf, readHRM1030_R01BlobAsBase64, readHRM1030_R01FileAsBase64,
    validateHRM1030_R01File, validateHRM1030_R01SaveData,
} from './Util_HRM1030_R01';

/** 세 그리드 참조와 화면 메시지 변경 함수이다. */
interface Props {
    gridRef: React.RefObject<AgGridReact<HRM1030_R01HeaderGridRow> | null>; // 사원 그리드
    trainingGridRef: React.RefObject<AgGridReact<HRM1030_R01TrainingDto> | null>; // 교육 그리드
    fileGridRef: React.RefObject<AgGridReact<HRM1030_R01FileDto> | null>; // 첨부 그리드
    setMessage: (value: string) => void; // 하단 메시지 설정
}

/** 이미지 확대 팝업(ImagePreviewModal)에 넘길 값이다. */
export interface HRM1030_R01ImagePreview {
    title: string; // 팝업 제목 (파일명)
    image: string; // data URL 접두사를 뗀 Base64 이미지
}

/**
 * 조회조건을 요청 파라미터로 바꾼다. 빈 값은 undefined로 넘겨 요청에서 뺀다(HRM1040과 같음).
 * @param filter 검색 조건
 * @returns 요청 파라미터
 */
const getHRM1030_R01Params = (filter: HRM1030_R01Filter): HRM1030_R01Params => ({
    trainYear: filter.trainYear || undefined,
    trainType: filter.trainType || undefined,
    compCd: filter.compCd || undefined,
    orgCd: filter.orgCd || undefined,
    empSts: filter.empSts || undefined,
    keyword: filter.keyword || undefined,
});

/**
 * 교육훈련 등록 화면의 조회, 행 선택, 첨부, 열람, 저장, 삭제, Excel 동작을 연결한다. 교육 행 추가는 그리드가 직접 처리한다.
 * @param gridRef 사원 그리드 참조
 * @param trainingGridRef 교육 그리드 참조. 저장 전 편집 확정에 쓴다.
 * @param fileGridRef 첨부 그리드 참조. 저장 전 편집 확정에 쓴다.
 * @param setMessage 조회·저장 결과를 표시하는 하단 메시지 변경 함수
 * @returns Container가 액션바·그리드·팝업에 연결할 동작 함수와 이미지 팝업 상태
 */
export const HRM1030_R01Content = ({ gridRef, trainingGridRef, fileGridRef, setMessage }: Props) => {
    // 공통 확인창(ConfirmModal)의 내용과 표시 여부를 바꾸는 함수를 가져온다.
    const { setConfirmModalProps, setConfirmModalVisible } = useUiuxStore();
    // 조회·저장 중 로딩 스피너 표시 함수를 가져온다.
    const { setLoadingSpinner } = useLoadingSpinnerStore();
    // 현재 사용자의 이 화면 액션바 버튼 권한을 읽어 온다.
    useFetchActionbarAuth('HRM1030_R01');
    // 이미지 확대 팝업에 표시할 파일. undefined이면 팝업을 닫는다.
    const [imagePreview, setImagePreview] = useState<HRM1030_R01ImagePreview | undefined>(undefined);

    /**
     * 사원 한 명의 교육 이력을 조회한다. 첨부 목록과 교육·첨부 편집 상태는 비운다.
     * @param empNo 조회할 사원번호. 없으면 목록을 비운다.
     * @param filter 교육년도·교육구분을 읽을 검색 조건
     * @returns 조회 완료를 나타내는 Promise<void>
     */
    const loadTrainingList = useCallback(async (empNo: string | undefined, filter: HRM1030_R01Filter) => {
        const state = useHRM1030_R01Store.getState();
        /** 교육 목록과 비교용 원본을 비운다. 조회 실패 시 이전 사원의 목록이 남지 않게 한다. */
        const clearTraining = () => {
            state.setHRM1030_R01TrainingList([]);
            state.setOriginalHRM1030_R01TrainingList([]);
        };
        if (!empNo) {
            clearTraining();
            return;
        }
        try {
            const response = await getHRM1030_R01TrainingList(empNo, {
                trainYear: filter.trainYear || undefined,
                trainType: filter.trainType || undefined,
            });
            if (!response.data.successFlag) {
                clearTraining();
                return;
            }
            state.setHRM1030_R01TrainingList(response.data.data);
            state.setOriginalHRM1030_R01TrainingList(response.data.data);
        } catch {
            // 요청 자체가 예외로 끝나도 목록을 비운다. 오류 알림은 공통 인터셉터가 표시한다.
            clearTraining();
        }
    }, []);

    /**
     * 검색 조건으로 사원과 건수를 조회하고 직전 사원 또는 첫 사원을 선택한다(HRM1040과 같은 흐름).
     * @param option search(새 조회, 스크롤 초기화) 또는 refetch(저장 후 재조회)
     * @param empNo 조회 후 선택할 사원번호. 없으면 직전 선택 사원을 다시 선택한다.
     * @param quiet true이면 조회 완료·빈 목록 알림을 표시하지 않는다.
     * @returns 조회 완료를 나타내는 Promise<void>
     */
    const executeDataFetch = useCallback(async (option: 'search' | 'refetch', empNo?: string, quiet = false) => {
        setLoadingSpinner(true);
        try {
            const state = useHRM1030_R01Store.getState();
            // 재조회(저장 후)는 마지막으로 조회한 조건을, 새 조회는 현재 입력 중인 조건을 쓴다.
            const filter = option === 'refetch' ? state.HRM1030_R01PastFilter : state.HRM1030_R01Filter;
            const params = getHRM1030_R01Params(filter);
            // ||: 지정한 사원이 없으면 직전 선택 사원을 다시 고른다.
            const previousEmpNo = empNo || state.selectedHRM1030_R01?.empNo;
            if (option === 'search') state.setScrollTop(null);
            const [rows, count] = await Promise.all([
                getHRM1030_R01List(option, params),
                getHRM1030_R01AllCount(params),
            ]);
            if (!rows.data.successFlag) return;
            state.setHRM1030_R01List(rows.data.data);
            // 목록 교체 후 valueIndex가 채워진 최신 목록에서 선택 사원을 찾는다.
            const current = useHRM1030_R01Store.getState();
            const selected = current.HRM1030_R01List.find(row => row.empNo === previousEmpNo)
                || current.HRM1030_R01List[0];
            current.setSelectedHRM1030_R01(selected);
            // 건수 조회가 실패하면 받은 목록 길이를 사용한다.
            const total = count.data.successFlag ? count.data.data : rows.data.data.length;
            current.setAllCount(total);
            current.setHRM1030_R01PastFilter(filter);
            await loadTrainingList(selected?.empNo, filter);
            if (!quiet) {
                if (rows.data.data.length) {
                    ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${rows.data.data.length}/${total}건)`);
                } else {
                    showWarning('데이터가 없습니다.');
                }
            }
        } finally {
            setLoadingSpinner(false);
        }
    }, [loadTrainingList, setLoadingSpinner, setMessage]);

    /**
     * 저장하지 않은 교육·첨부 변경 여부를 확인한다. 교육과정명이 빈 신규 행은 변경으로 보지 않는다.
     * @returns D 표시, 수정 행, 이름이 입력된 신규 교육, 신규·수정 첨부 중 하나라도 있으면 true
     */
    const hasChanges = useCallback((): boolean => {
        const state = useHRM1030_R01Store.getState();
        return state.deletedHRM1030_R01TrainingList.length > 0
            || state.deletedHRM1030_R01FileList.length > 0
            || state.HRM1030_R01TrainingList.some(row => row.isModified || (row.inserted && !!row.trainNm.trim()))
            || state.HRM1030_R01FileList.some(row => row.inserted || row.isModified);
    }, []);

    /**
     * 변경이 있으면 공통 확인창으로 버릴지 묻고 다음 동작을 실행한다(HRM1040과 같음).
     * @param onConfirm 확인했을 때(또는 변경이 없을 때) 실행할 동작
     */
    const confirmDiscard = useCallback((onConfirm: () => void) => {
        if (!hasChanges()) {
            onConfirm();
            return;
        }
        const close = () => {
            setConfirmModalVisible(false);
            setConfirmModalProps(undefined);
        };
        setConfirmModalProps({
            title: '변경 내용 확인',
            message: '저장하지 않은 변경 내용이 있습니다.\n변경 내용을 버리고 진행하시겠습니까?',
            confirmText: '확인',
            cancelText: '취소',
            onConfirm: () => {
                close();
                onConfirm();
            },
            onCancel: close,
        });
        setConfirmModalVisible(true);
    }, [hasChanges, setConfirmModalProps, setConfirmModalVisible]);

    /** 액션바 조회에서 실행한다. 변경이 있으면 확인창을 먼저 띄운다. */
    const handleFetchClick = useCallback(() => {
        confirmDiscard(() => {
            void executeDataFetch('search');
        });
    }, [confirmDiscard, executeDataFetch]);

    /**
     * 사원 행을 바꾸면서 새 사원의 교육 이력을 읽는다. 같은 사원을 다시 누르면 무시한다.
     * @param row 클릭한 사원 행
     */
    const handleHeaderRowClick = useCallback((row: HRM1030_R01HeaderDto) => {
        if (row.empNo === useHRM1030_R01Store.getState().selectedHRM1030_R01?.empNo) return;
        confirmDiscard(() => {
            const state = useHRM1030_R01Store.getState();
            state.setSelectedHRM1030_R01(row);
            void loadTrainingList(row.empNo, state.HRM1030_R01PastFilter);
        });
    }, [confirmDiscard, loadTrainingList]);

    /**
     * 교육 행을 첨부 대상으로 정하고, 저장된 교육이면 처음 한 번만 첨부 목록을 불러온다.
     * 이미 불러온 교육은 화면에 있는 첨부(신규·D 표시 포함)를 그대로 쓴다.
     * @param row 클릭한 교육 행
     * @returns 조회 완료를 나타내는 Promise<void>
     */
    const handleTrainingSelect = useCallback(async (row: HRM1030_R01TrainingDto) => {
        const state = useHRM1030_R01Store.getState();
        if (state.selectedHRM1030_R01Training !== row.valueIndex) {
            state.setSelectedHRM1030_R01Training(row.valueIndex);
            state.setSelectedHRM1030_R01FileList([]);
        }
        const rowId = String(row.valueIndex);
        if (row.inserted || row.trainSeq == null || state.loadedHRM1030_R01FileTrainingList.includes(rowId)) return;
        state.addLoadedHRM1030_R01FileTrainingList(rowId);
        const empNo = state.selectedHRM1030_R01?.empNo;
        if (!empNo) return;
        try {
            const response = await getHRM1030_R01FileList(empNo, row.trainSeq);
            if (!response.data.successFlag) return;
            // 서버 첨부에 소속 교육 행 번호를 붙여 목록에 더한다.
            useHRM1030_R01Store.getState().appendHRM1030_R01FileList(response.data.data.map(file => ({
                ...file,
                trainingValueIndex: row.valueIndex,
                inserted: false,
                isModified: false,
            })));
        } catch {
            // 조회 실패 알림은 공통 인터셉터가 표시한다. 이 교육의 첨부 목록은 빈 상태로 둔다.
        }
    }, []);

    /**
     * 끌어놓기·파일 선택으로 받은 파일을 검사하고 첨부 대상 교육의 신규 첨부로 추가한다.
     * @param files 받은 파일 목록
     * @returns 추가 완료를 나타내는 Promise<void>
     */
    const handleFilesAdd = useCallback(async (files: File[]) => {
        const state = useHRM1030_R01Store.getState();
        const target = state.HRM1030_R01TrainingList.find(row => row.valueIndex === state.selectedHRM1030_R01Training);
        if (!target) {
            showWarning('수료증을 붙일 교육훈련 행을 먼저 선택하세요.');
            return;
        }
        if (state.deletedHRM1030_R01TrainingList.includes(String(target.valueIndex))) {
            showWarning('삭제 표시된 교육에는 첨부할 수 없습니다.');
            return;
        }
        // 제외한 파일과 사유 목록 (예: "a.webp(형식)")
        const rejected: string[] = [];
        // 통과한 파일을 Base64로 읽은 신규 첨부 행 목록
        const newRows: HRM1030_R01FileDto[] = [];
        for (const file of files) {
            const reason = validateHRM1030_R01File(file);
            if (reason) {
                rejected.push(`${file.name}(${reason})`);
                continue;
            }
            const uploadFileData = await readHRM1030_R01FileAsBase64(file);
            newRows.push({
                ...defaultHRM1030_R01FileDto,
                empNo: target.empNo,
                trainSeq: target.trainSeq,
                fileNm: file.name,
                fileType: getHRM1030_R01MimeType(file.name),
                fileSize: file.size,
                trainingValueIndex: target.valueIndex,
                uploadFileData,
                inserted: true,
            });
        }
        if (newRows.length) state.appendHRM1030_R01FileList(newRows);
        // 삼항 연산자: 제외한 파일이 있을 때만 사유를 덧붙인다.
        const rejectedText = rejected.length ? ` (제외: ${rejected.join(', ')})` : '';
        if (newRows.length) {
            setMessage(`수료증 ${newRows.length}건을 첨부했습니다${rejectedText}. 저장 버튼을 눌러 반영하세요.`);
        } else {
            showWarning(`첨부하지 못했습니다: ${rejected.join(', ')} — PNG·JPG·GIF 이미지 또는 PDF, 10MB 이하만 됩니다.`);
        }
    }, [setMessage]);

    /**
     * 첨부 파일 내용을 Blob으로 가져온다. 저장 전 첨부는 들고 있는 Base64로, 저장된 첨부는 다운로드 API로 만든다.
     * @param row 첨부 행
     * @returns 파일 Blob. 가져오지 못하면 undefined
     */
    const getFileBlob = useCallback(async (row: HRM1030_R01FileDto): Promise<Blob | undefined> => {
        if (row.uploadFileData) return base64ToBlob(row.uploadFileData, getHRM1030_R01MimeType(row.fileNm));
        if (!row.filePath) return undefined;
        const response = await downloadHRM1030_R01File(row.filePath);
        // 삼항 연산자: 응답이 Blob이 아니면 Blob으로 감싼다(EQM3020과 같음).
        return response.data instanceof Blob ? response.data : new Blob([response.data]);
    }, []);

    /**
     * 첨부를 연다. 이미지는 공통 ImagePreviewModal, PDF는 새 탭(TQC1060과 같은 방식)으로 연다.
     * @param row 클릭한 첨부 행
     * @returns 열람 완료를 나타내는 Promise<void>
     */
    const handleFileOpen = useCallback(async (row: HRM1030_R01FileDto) => {
        try {
            setLoadingSpinner(true);
            const blob = await getFileBlob(row);
            if (!blob) {
                showWarning('열람할 파일이 없습니다.');
                return;
            }
            if (isHRM1030_R01Pdf(row)) {
                const fileUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
                window.open(fileUrl, '_blank');
                // 새 탭이 파일을 읽을 시간을 준 뒤 임시 주소를 해제한다.
                setTimeout(() => URL.revokeObjectURL(fileUrl), 5000);
                return;
            }
            // 삼항 연산자: 저장 전 이미지는 Base64를 그대로, 저장된 이미지는 Blob을 Base64로 바꿔 넘긴다.
            const image = row.uploadFileData ? row.uploadFileData : await readHRM1030_R01BlobAsBase64(blob);
            setImagePreview({ title: row.fileNm, image });
        } catch {
            showWarning('파일 열람 중 오류가 발생했습니다.');
        } finally {
            setLoadingSpinner(false);
        }
    }, [getFileBlob, setLoadingSpinner]);

    /**
     * 저장된 첨부를 원래 파일명으로 내려받는다(EQM3020 handleFileDownload와 같은 방식).
     * @param row 다운로드할 첨부 행
     * @returns 다운로드 완료를 나타내는 Promise<void>
     */
    const handleFileDownload = useCallback(async (row: HRM1030_R01FileDto) => {
        if (!row.filePath) {
            showWarning('다운로드할 파일이 없습니다.');
            return;
        }
        try {
            const blob = await getFileBlob(row);
            if (blob) downloadExcel(blob, row.fileNm || `training_file_${row.filePath}`);
        } catch {
            showWarning('파일 다운로드 중 오류가 발생했습니다.');
        }
    }, [getFileBlob]);

    /**
     * 마지막 작업 그리드(교육 또는 첨부)에서 선택한 신규 행은 제거하고 저장된 행은 D 표시한다.
     * D 표시가 이미 있으면 삭제를 다시 눌렀을 때 표시를 모두 복구한다(HRM1040과 같음).
     */
    const handleDeleteClick = useCallback(() => {
        const state = useHRM1030_R01Store.getState();
        if (state.activeGridType === 'file') {
            if (state.deletedHRM1030_R01FileList.length) {
                state.setDeletedHRM1030_R01FileList([]);
                state.setSelectedHRM1030_R01FileList([]);
                return;
            }
            const selected = state.selectedHRM1030_R01FileList;
            state.HRM1030_R01FileList
                .filter(row => selected.includes(String(row.valueIndex)) && row.inserted)
                .forEach(row => state.removeHRM1030_R01FileList(row.valueIndex));
            state.setDeletedHRM1030_R01FileList(selected.filter(id =>
                state.HRM1030_R01FileList.some(row => String(row.valueIndex) === id && !row.inserted),
            ));
            state.setSelectedHRM1030_R01FileList([]);
            return;
        }
        if (state.deletedHRM1030_R01TrainingList.length) {
            state.setDeletedHRM1030_R01TrainingList([]);
            state.setSelectedHRM1030_R01TrainingList([]);
            return;
        }
        const selected = state.selectedHRM1030_R01TrainingList;
        state.HRM1030_R01TrainingList
            .filter(row => selected.includes(String(row.valueIndex)) && row.inserted)
            .forEach(row => state.removeHRM1030_R01TrainingList(row.valueIndex));
        state.setDeletedHRM1030_R01TrainingList(selected.filter(id =>
            state.HRM1030_R01TrainingList.some(row => String(row.valueIndex) === id && !row.inserted),
        ));
        state.setSelectedHRM1030_R01TrainingList([]);
    }, []);

    /**
     * 교육·첨부 변경을 검증한 뒤 한 요청으로 저장한다.
     * D 표시 교육은 삭제 목록으로, 신규·수정 교육과 첨부 변경이 있는 교육은 저장 목록으로 보낸다.
     * 교육과정명이 빈 신규 행은 보내지 않는다. 요청에 없는 행은 서버가 건드리지 않는다.
     * @returns 저장과 재조회 완료를 나타내는 Promise<void>
     */
    const handleSaveClick = useCallback(async () => {
        gridRef.current?.api.stopEditing();
        trainingGridRef.current?.api.stopEditing();
        fileGridRef.current?.api.stopEditing();
        const state = useHRM1030_R01Store.getState();
        const empNo = state.selectedHRM1030_R01?.empNo;
        if (!empNo) {
            showWarning('사원을 먼저 선택하세요.');
            return;
        }
        if (!hasChanges()) {
            showWarning('저장할 내용이 없습니다.');
            return;
        }
        const deletedTrainingIds = state.deletedHRM1030_R01TrainingList;
        const deletedFileIds = state.deletedHRM1030_R01FileList;
        // D 표시된 저장된 교육. 서버가 첨부 파일까지 함께 삭제한다.
        const deletedTrainingList = state.HRM1030_R01TrainingList
            .filter(row => deletedTrainingIds.includes(String(row.valueIndex)) && !row.inserted);
        // D 표시가 아니고, 교육과정명이 빈 신규 행이 아닌 교육
        const liveTrainingList = state.HRM1030_R01TrainingList.filter(row =>
            !deletedTrainingIds.includes(String(row.valueIndex))
            && !(row.inserted && !row.trainNm.trim()));
        /**
         * 교육 한 건에 속한 첨부 변경을 모은다.
         * @param row 교육 행
         * @returns 신규·비고 수정 첨부와 D 표시 첨부
         */
        const getFileChanges = (row: HRM1030_R01TrainingDto) => {
            const files = state.HRM1030_R01FileList.filter(file => file.trainingValueIndex === row.valueIndex);
            return {
                saveFiles: files.filter(file => !deletedFileIds.includes(String(file.valueIndex)) && (file.inserted || file.isModified)),
                deletedFiles: files.filter(file => deletedFileIds.includes(String(file.valueIndex)) && !file.inserted),
            };
        };
        // 저장 목록: 신규·수정 교육과, 교육은 그대로지만 첨부 변경이 있는 교육
        const saveTrainingList = liveTrainingList
            .map(row => ({ row, ...getFileChanges(row) }))
            .filter(item => item.row.inserted || item.row.isModified || item.saveFiles.length || item.deletedFiles.length);
        const validationMessage = validateHRM1030_R01SaveData(saveTrainingList.map(item => item.row));
        if (validationMessage) {
            showWarning(validationMessage);
            return;
        }
        // 저장 후 다시 선택할 첨부 대상 교육. 다시 조회하면 화면 행 번호가 새로 매겨지므로 순번 등으로 다시 찾는다.
        const targetTraining = state.HRM1030_R01TrainingList.find(row => row.valueIndex === state.selectedHRM1030_R01Training);
        // true이면 첨부 대상 교육이 이번 저장에서 삭제되어 다시 선택하지 않는다.
        const isTargetDeleted = !!targetTraining && deletedTrainingIds.includes(String(targetTraining.valueIndex));
        // 저장 전 교육 순번 목록. 신규 교육이 서버에서 받은 순번을 찾을 때 쓴다.
        const previousTrainSeqs = state.HRM1030_R01TrainingList
            .map(row => row.trainSeq)
            .filter((trainSeq): trainSeq is number => trainSeq != null);
        setLoadingSpinner(true);
        try {
            const filter = state.HRM1030_R01PastFilter;
            // 신규 교육은 순번을 null로 보내 서버가 사원별 순번을 새로 매기게 한다.
            const response = await patchHRM1030_R01({
                empNo,
                trainYear: filter.trainYear || undefined,
                trainType: filter.trainType || undefined,
                hrmTrainingDtoList: saveTrainingList.map(item => ({
                    ...item.row,
                    // 삼항 연산자: 신규 교육이면 순번을 비운다.
                    trainSeq: item.row.inserted ? null : item.row.trainSeq,
                    hrmTrainingFileDtoList: item.saveFiles.map(file => ({
                        ...file,
                        fileSeq: file.inserted ? null : file.fileSeq,
                    })),
                    deletedHrmTrainingFileDtoList: item.deletedFiles,
                })),
                deletedHrmTrainingDtoList: deletedTrainingList,
            });
            if (!response.data.successFlag) return;
            showSuccess('교육훈련 저장이 완료되었습니다.');
            setMessage('교육훈련 저장이 완료되었습니다.');
            await executeDataFetch('refetch', empNo, true);
            // 다시 조회하면 첨부 대상과 첨부 목록이 비워지므로, 저장 전에 보던 교육을 다시 선택하고 첨부를 다시 불러온다.
            if (targetTraining && !isTargetDeleted) {
                const current = useHRM1030_R01Store.getState();
                // 저장된 교육은 같은 순번으로, 신규 교육은 새로 생긴 순번 중 교육과정명·시작일이 같은 행으로 찾는다.
                const reselected = targetTraining.inserted
                    ? current.HRM1030_R01TrainingList.find(row => row.trainSeq != null
                        && !previousTrainSeqs.includes(row.trainSeq)
                        && row.trainNm === targetTraining.trainNm
                        && row.startDt === targetTraining.startDt)
                    : current.HRM1030_R01TrainingList.find(row => row.trainSeq === targetTraining.trainSeq);
                if (reselected) {
                    current.setSelectedHRM1030_R01TrainingList([String(reselected.valueIndex)]);
                    await handleTrainingSelect(reselected);
                }
            }
        } finally {
            setLoadingSpinner(false);
        }
    }, [gridRef, trainingGridRef, fileGridRef, hasChanges, executeDataFetch, handleTrainingSelect, setLoadingSpinner, setMessage]);

    /**
     * 마지막으로 작업한 그리드 기준으로 서버 Excel을 내려받는다. 교육 그리드면 선택 사원의 교육 이력, 그 외는 사원 목록이다.
     * @returns 다운로드 완료를 나타내는 Promise<void>
     */
    const handleExcelClick = useCallback(async () => {
        const state = useHRM1030_R01Store.getState();
        const filter = state.HRM1030_R01PastFilter;
        const empNo = state.selectedHRM1030_R01?.empNo;
        if (state.activeGridType === 'training' && empNo) {
            const response = await downloadExcel_HRM1030_R01Training(empNo, {
                trainYear: filter.trainYear || undefined,
                trainType: filter.trainType || undefined,
            });
            if (!response.data.successFlag) return;
            downloadSettingExcel({ responseData: response.data.data, fileName: `교육훈련이력_${empNo}-excel.xlsx` });
            return;
        }
        if (!state.HRM1030_R01List.length) {
            showWarning('먼저 조회하세요.');
            return;
        }
        const response = await downloadExcel_HRM1030_R01(getHRM1030_R01Params(filter));
        if (!response.data.successFlag) return;
        downloadSettingExcel({ responseData: response.data.data, fileName: '교육훈련_사원목록-excel.xlsx' });
    }, []);

    /**
     * 화면 조건·목록·메시지를 모두 빈 상태로 초기화한다. 변경이 있으면 확인창을 먼저 띄운다.
     * 초기화 후에는 자동으로 다시 조회하지 않고, 사용자가 조회 버튼을 눌러야 다시 조회한다(HRM1040과 같음).
     */
    const initContainerData = useCallback(() => {
        confirmDiscard(() => {
            useHRM1030_R01Store.getState().initData();
            setMessage('');
        });
    }, [confirmDiscard, setMessage]);

    return {
        fetchData: handleFetchClick,
        handleHeaderRowClick,
        handleTrainingSelect,
        handleFilesAdd,
        handleFileOpen,
        handleFileDownload,
        handleDeleteClick,
        handleSaveClick,
        handleExcelClick,
        initContainerData,
        imagePreview,
        closeImagePreview: () => setImagePreview(undefined),
    };
};
